import axios from 'axios';
import { api } from '../services/api';
import { ITrackingHeaders } from '../services/tracking';
import {
  IFaturamentoResponse,
  IFolhaPorMesResponse,
  IImpostosTotals,
  ICreateLinkParams,
  ICreateLinkResponse,
  IAccessLinkParams,
  IAccessLinkResponse,
} from '../pages/modulesHome/dossieEmpresa/utils/dossieTypes';

interface IFaturamentoParams {
  external_id: string;
  date_start?: string;
  date_end?: string;
}

interface IImpostosParams {
  external_id: string;
  date_start?: string;
  date_end?: string;
  screen_data: 'simples_nacional' | 'fiscal';
  regime?: 'lucro_presumido' | 'lucro_real';
}

interface IFolhaPorMesParams {
  external_id: string;
  ano?: number;
}

export const useDossie = () => {
  /**
   * Lista faturamento por empresa com filtro por período
   * GET /api/v1/empresas/faturamento/{codi_emp}/list/
   *
   * A nova rota aceita date_start e date_end como query params e filtra no backend
   */
  const getFaturamentoList = async (params: IFaturamentoParams): Promise<IFaturamentoResponse> => {
    const res = await api.get<IFaturamentoResponse>(
      `/api/v1/empresas/faturamento/${params.external_id}/list/`,
      {
        params: {
          external_id: params.external_id,
          date_start: params.date_start,
          date_end: params.date_end,
        },
      },
    );
    return res.data;
  };

  /**
   * Totais de impostos (agregado)
   * GET /api/v1/empresas/impostos/total/
   *
   * Rota muito estranha, ela não retorna o padrão das outras, com { data: etc} retorna direto o
   *
   */
  const getImpostosTotais = async (params: IImpostosParams): Promise<IImpostosTotals> => {
    const res = await api.get<IImpostosTotals>('/api/v1/empresas/impostos/total/', {
      params,
    });
    return res.data;
  };

  /**
   * Lista folha de pagamento por mês
   * GET /api/v1/empresa-detalhamento/{external_id}/folha-pagamento/
   *
   * Retorna dados de folha agrupados por mês (admissões, demissões, férias, afastamentos, fgts, dctfweb)
   */
  const getFolhaPorMes = async (params: IFolhaPorMesParams): Promise<IFolhaPorMesResponse> => {
    const res = await api.get<IFolhaPorMesResponse>(
      `/api/v1/empresa-detalhamento/${params.external_id}/folha-pagamento/`,
      {
        params: {
          ano: params.ano,
        },
      },
    );
    return res.data;
  };

  /**
   * Determina os parâmetros de regime tributário baseado no regime da empresa
   */
  const getTaxParams = (
    regime: string | undefined,
  ): Pick<IImpostosParams, 'screen_data' | 'regime'> => {
    const normalizedRegime = regime?.toLowerCase() || '';

    if (normalizedRegime.includes('simples nacional') || normalizedRegime.includes('simples')) {
      return { screen_data: 'simples_nacional' };
    }

    if (normalizedRegime.includes('lucro presumido') || normalizedRegime.includes('presumido')) {
      return { screen_data: 'fiscal', regime: 'lucro_presumido' };
    }

    if (normalizedRegime.includes('lucro real') || normalizedRegime.includes('real')) {
      return { screen_data: 'fiscal', regime: 'lucro_real' };
    }

    return { screen_data: 'fiscal' };
  };

  /**
   * Criar link temporário para compartilhamento (autenticado)
   * POST /api/v1/clientes/links-temporarios/
   */
  const createTemporaryLink = async (params: ICreateLinkParams): Promise<ICreateLinkResponse> => {
    const res = await api.post<ICreateLinkResponse>('/api/v1/clientes/links-temporarios/', params);
    return res.data;
  };

  /**
   * Acessar dados via link temporário (público - sem autenticação)
   * GET /api/v1/clientes/links-temporarios/access/{hashed_link}
   *
   * A senha vai no header X-Access-Password (não na query).
   *
   * Parâmetros (query):
   * - date_start, date_end: Filtro para impostos e faturamento
   * - ano_folha_pagamento: Filtro exclusivo para folha de pagamento
   *
   * Headers opcionais de tracking:
   * - X-Client-Latitude, X-Client-Longitude: Geolocalização GPS (se permitido)
   * - X-Client-Geo-Accuracy: Precisão da geolocalização em metros
   * - X-Client-Screen-Width, X-Client-Screen-Height: Dimensões da tela
   * - X-Client-Timezone: Timezone do cliente
   * - X-Client-Language: Idioma do navegador
   */
  const accessByTemporaryLink = async (
    params: IAccessLinkParams,
    trackingHeaders?: ITrackingHeaders,
  ): Promise<IAccessLinkResponse> => {
    // Usa axios diretamente para não passar token de autenticação.
    // Senha vai como header X-Access-Password para não vazar em logs de
    // proxy/CDN, histórico do browser e Referer (backend ainda aceita query
    // ?password= como legacy, mas grava logger.warning). Apontado no PR #569.
    const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const res = await axios.get<IAccessLinkResponse>(
      `${baseUrl}/api/v1/clientes/links-temporarios/access/${params.hashed_link}`,
      {
        params: {
          date_start: params.date_start,
          date_end: params.date_end,
          ano_folha_pagamento: params.ano_folha_pagamento,
        },
        headers: {
          ...trackingHeaders,
          'X-Access-Password': params.password,
        },
      },
    );
    return res.data;
  };

  return {
    getFaturamentoList,
    getImpostosTotais,
    getFolhaPorMes,
    getTaxParams,
    createTemporaryLink,
    accessByTemporaryLink,
  };
};
